import React, { useEffect, useState, useContext, useRef } from "react";
import Page from "components/ui/Page";
import Header from "components/ui/Header";
import { Box, Flex, Grid, Link, Spinner } from "theme-ui";
import { BackArrowIcon, EllipsisIcon } from "components/svg";
import { getInvoice } from "api/Invoices";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Tab, Tabs } from "components/ui/Tabs";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import { CheckMarkIcon, TrashIcon, ProductsIcon, PlusIcon } from "components/svg";
import Separator from "components/ui/Separator";
import SelectInput from "components/forms/SelectInput";
import MultiText from "components/forms/MultiText";
import { getClients } from "api/Clients";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import UserContext from "context/User";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import P from "components/typography/P";
import { Button } from "theme-ui";
import { getDepot } from "api/Providers/Depots";
import SearchField from "components/forms/SearchField";
import Checkbox from "components/forms/Checkbox";
import { updateInvoice } from "api/Invoices";
import { useToast } from "components/toast";
import moment from "moment";
import { trackActivity } from "api/ActivityTracker";

interface Product {
  id: string;
  name: string;
  addon: boolean;
  unit?: string;
  baseCost?: number;
  quantity?: number;
  entityId?: string;
  description?: string;
  markup?: number;
  cost?: number;
  price?: number;
  position?: number;
}

interface Depot {
  id: string;
  name: string;
  provider: { name: string };
  products: Product[];
}

interface InvoiceItem {
  id?: string;
  quantity: number;
  type?: string;
  entityId?: string;
  depotId: string;
  markup: number;
  cost: number;
}

export interface UpdateInvoiceData {
  projectName?: string;
  serviceDate?: string;
  discountAmount?: number; // dollar value
  discountTotal?: number; // total after discount
  discountFlag?: boolean;
  primaryClient?: { id: string };
  dueDate?: string;
  contactsCC?: string[];
  contactsBCC?: string[];
  items?: InvoiceItem[][]; // Array of arrays
  allowPartialPayment?: boolean;
  partialPaymentPercent?: number;
}

const InvoiceEdit = () => {
  const [invoice, setInvoice] = useState<any>(null);
  const [companyClientOptions, setCompanyClientOptions] = useState<any>([]);
  const [tableItems, setTableItems] = useState<any[]>([]);
  const [originalTableItems, setOriginalTableItems] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedDepot, setSelectedDepot] = useState<Depot | null>(null);
  const [depotServiceOptions, setDepotServiceOptions] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedAddons, setSelectedAddons] = useState<any[]>([]);
  const [canOpenEditModal, setCanOpenEditModal] = useState<boolean>(false);
  
  const [addOnOpen, setAddOnOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newAddons, setNewAddons] = useState<any[]>([]);
  const [discount, setDiscount] = useState<any>(null);
  const [dueDatePreset, setDueDatePreset] = useState<string>("");

  const columnHelper = createColumnHelper();
  const { user } = useContext<any>(UserContext);
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const hasTrackedView = useRef(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Invoice";
    _init();
  }, []);

  const _init = async () => {
    const invoiceRes = await getInvoice({ data: { id: id } });

    if (invoiceRes.id) {
      setInvoice(invoiceRes);
      // Determine which due date preset matches
      if (invoiceRes.dueDate) {
        const due = moment.utc(invoiceRes.dueDate);
        const anchor = invoiceRes.sentDate ? moment.utc(invoiceRes.sentDate) : moment().startOf("day");
        const diffDays = Math.round(due.diff(anchor, "days", true));
        if (diffDays === 30) {
          setDueDatePreset("net30");
        } else if (diffDays === 60) {
          setDueDatePreset("net60");
        } else if (diffDays === 90) {
          setDueDatePreset("net90");
        } else {
          setDueDatePreset("custom");
        }
      } else {
        setDueDatePreset("");
      }
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "invoices",
          action: "view_invoice_edit",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "invoice",
          entityId: invoiceRes.id,
          metadata: null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
      const allItems = invoiceRes.items?.flat() || [];

      const groupedByItemGroup: Record<number, any[]> = {};
      allItems.forEach((item: any) => {
        if (!groupedByItemGroup[item.itemGroup]) {
          groupedByItemGroup[item.itemGroup] = [];
        }
        groupedByItemGroup[item.itemGroup].push(item);
      });

      const structuredItems = Object.values(groupedByItemGroup).map((group: any[]) => {
        const service = group.find((item) => item.type === "service");
        const product = group.find((item) => item.type === "product");
        const addons = group.filter((item) => item.type === "addon-product");

        return {
          ...service,
          product: product
            ? {
                id: product.id,
                name: product.name,
                description: product.description,
                unit: product.unit,
                entityId: product.entityId,
              }
            : null,
          cost: product?.cost ?? 0,
          markup: product?.markup ?? 0,
          price: product?.price ?? 0,
          quantity: product?.quantity ?? 0,
          serviceId: service?.entityId,
          addons: addons.map((addon) => ({
            id: addon.id,
            name: addon.name,
            description: addon.description,
            unit: addon.unit,
            baseCost: addon.cost,
            markup: addon.markup,
            price: addon.price,
            quantity: addon.quantity,
            position: addon.position,
            entityId: addon.entityId,
          })),
        };
      });

      setTableItems(structuredItems);
      setOriginalTableItems(structuredItems);
    }

    // On first load, set discount from API:
    if (invoiceRes.discountAmount) {
      setDiscount({
        amount: invoiceRes.discountAmount, // dollar value
        total: invoiceRes.discountTotal, // total after discount
        discountName: invoiceRes.discount.name,
        discountId: invoiceRes.discount.id,
        discountPercent: invoiceRes.discount.amount, // percent
      });
    }

    const clientRes = await getClients({});
    setCompanyClientOptions(clientRes.items.map((p: any) => ({ value: p.id, label: p.name })));
  };

  const submitUpdate = async (values: any) => {
    const mappedItems = tableItems.map((service) => {
      console.log("service", service);

      const addonItems = (service.addons || [])
        .filter((addon: any) => addon.quantity > 0)
        .map((addon: any) => ({
          ...(addon.new ? {} : { id: addon.id }),
          quantity: addon.quantity,
          cost: Number(addon.baseCost),
          price: Number(addon.price),
          depotId: service.depot?.id,
          entityId: addon.entityId,
          type: "addon-product",
        }));

      const serviceItem = {
        id: service.id,
        quantity: 1,
        cost: 0,
        markup: 0,
        depotId: service.depot?.id,
        entityId: service.entityId,
        type: "service",
      };

      const productItem = {
        id: service.product.id,
        quantity: service.quantity,
        cost: Number(service.cost),
        price: Number(service.price),
        depotId: service.depot?.id,
        entityId: service.product.entityId,
        type: "product",
      };

      return [serviceItem, productItem, ...addonItems];
    });

    const servicesChanged =
      tableItems.length !== originalTableItems.length ||
      tableItems.some((current) => {
        const original = originalTableItems.find((s) => s.id === current.id);
        if (!original) return true;

        const fieldsMatch =
          original.quantity === current.quantity &&
          Number(original.price) === Number(current.price) &&
          Number(original.cost) === Number(current.cost) &&
          original.depot?.id === current.depot?.id &&
          original.serviceId === current.serviceId;

        const currentAddons = (current.addons || []).filter((a: any) => a.quantity > 0);
        const originalAddons = (original.addons || []).filter((a: any) => a.quantity > 0);

        const addonsMatch =
          currentAddons.length === originalAddons.length &&
          currentAddons.every((a: any) => {
            const match = originalAddons.find((o: any) => o.entityId === a.entityId);
            return match && a.quantity === match.quantity && Number(a.cost) === Number(match.cost) && Number(a.price) === Number(match.price);
          });

        return !(fieldsMatch && addonsMatch);
      });

    const newDiscountAmount = discount?.amount || 0;
    const newDiscountTotal = discount?.total || 0;

    
    const updateData: UpdateInvoiceData = {
      projectName: values.projectName,
      primaryClient: { id: values.primaryClient },
      contactsCC: values.contactsCC,
      contactsBCC: values.contactsBCC,
      discountAmount: discount ? newDiscountAmount : undefined,
      discountTotal: discount ? newDiscountTotal : undefined,
      allowPartialPayment: values.allowPartialPayment,
      partialPaymentPercent: values.allowPartialPayment ? values.partialPaymentPercent : 75,
      ...(servicesChanged ? { items: mappedItems } : {}),
    };

    if (values.dueDate) {
      updateData.dueDate = moment.unix(values.dueDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }

    console.log("updateData", updateData);

    const res = await updateInvoice({ data: { id }, update: updateData });

    if (res.id) {
      const changedFields: string[] = [];

      if (values.projectName !== invoice.project.projectName) changedFields.push("projectName");
      if (values.primaryClient !== invoice.primaryClient?.id) changedFields.push("primaryClient");

      const originalContactsCC = invoice.contactsCC || [];
      const originalContactsBCC = invoice.contactsBCC || [];
      const currentContactsCC = values.contactsCC || [];
      const currentContactsBCC = values.contactsBCC || [];

      if (JSON.stringify(currentContactsCC) !== JSON.stringify(originalContactsCC)) changedFields.push("contactsCC");
      if (JSON.stringify(currentContactsBCC) !== JSON.stringify(originalContactsBCC)) changedFields.push("contactsBCC");

      if (values.dueDate) {
        const newDueDate = moment.unix(values.dueDate).format("YYYY-MM-DD");
        const oldDueDate = invoice.dueDate ? moment(invoice.dueDate).format("YYYY-MM-DD") : null;
        if (newDueDate !== oldDueDate) {
          changedFields.push("dueDate");
        }
      } else if (!values.dueDate && invoice.dueDate) {
        changedFields.push("dueDate");
      }

      if (discount) {
        const originalDiscountAmount = invoice.discountAmount || 0;
        const originalDiscountTotal = invoice.discountTotal || 0;
        if (Number(discount.amount || 0) !== Number(originalDiscountAmount)) changedFields.push("discountAmount");
        if (Number(discount.total || 0) !== Number(originalDiscountTotal)) changedFields.push("discountTotal");
      } else if (invoice.discountAmount || invoice.discountTotal) {
        // Discount removed
        changedFields.push("discountAmount");
        changedFields.push("discountTotal");
      }

      if (servicesChanged) changedFields.push("items");

      if (changedFields.length > 0) {
        void trackActivity({
          module: "invoices",
          action: "update_invoice",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "invoice",
          entityId: res.id,
          metadata: { changedFields },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }

      setInvoice(res);
      toast.add("Invoice updated.", "var(--theme-ui-colors-success)");
      navigate(`/invoices/${res.id}`);
    }
  };

  const _validate = Yup.object().shape({
    projectName: Yup.string().required("Project name is required."),
  });

  const toggleEditModal = (item?: any) => {
    if (item) {
      
      const bulkProductId = item.product.id;
      const addOnIds = item.addons.map((addon: any) => addon.addonId);
      const combinedProductIds = [bulkProductId, ...addOnIds].join(",");

      if (user.role !== null) {
        getDepotDetails(item.depot.id, item.depot.provider.id);
      } else {
        setSelectedDepot(item.depot);
        setDepotServiceOptions([{ value: item.serviceId, label: item.name }]);
      }

      setSelectedRow(item);
      setSelectedAddons(item.addons);
      setSelectedService(item.serviceId);
      setCanOpenEditModal(true);
    } else {
      setSelectedRow(null);
      setSelectedDepot(null);
      setEditModalOpen(false);
      setCanOpenEditModal(false);
    }
  };

  const getDepotDetails = async (depotId: string, providerId: string) => {
    const depotRes = await getDepot({ data: { id: depotId }, provider: { id: providerId } });

    if (depotRes.id) {
      setSelectedDepot(depotRes);

      const serviceOptions = depotRes.services.map((service: any) => ({
        value: service.id,
        label: service.name,
      }));

      setDepotServiceOptions(serviceOptions);
    }
  };

  const submitRemoveItem = (item: any) => {
    console.log("Remove item", item);
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "relatedService",
      cell: (info: any) => <Box sx={{ color: "#000", fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Related Service</span>,
    }),
    columnHelper.accessor((row: any) => row?.product.name, {
      id: "productName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Bulk Product</span>,
    }),
    columnHelper.accessor((row: any) => row?.addons, {
      id: "addons",
      cell: (info: any) => {
        const addons = info.getValue();
        return Array.isArray(addons) && addons.filter((addon: any) => addon.quantity > 0).length > 0
          ? addons
              .filter((addon: any) => addon.quantity > 0)
              .map((addon: any) => addon.name)
              .join(", ")
          : "None";
      },
      header: () => <span>Add-on Products</span>,
    }),

    columnHelper.accessor((row: any) => row?.depot.provider.name, {
      id: "providerName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Provider</span>,
    }),

    columnHelper.accessor((row: any) => "actions", {
      id: "actions",
      cell: (info: any) => {
        const items = [
          { label: "Edit", onClick: () => toggleEditModal(info.row.original) },
          ...(tableItems.length > 1 ? [{ label: "Remove", onClick: () => submitRemoveItem(info.row.original), style: { color: "var(--theme-ui-colors-danger)" } }] : []),
        ];

        return (
          <Box>
            <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  const submitItemEdit = async (values: any) => {
    const relatedServiceOption = depotServiceOptions.find((option) => option.value === values.relatedService);

    const relatedServiceLabel = relatedServiceOption?.label || "";

    const depotRes = selectedDepot || selectedRow.depot;

    const services = [
      {
        id: selectedRow.id,
        entityId: values.relatedService,
        quantity: values.quantity,
        price: values.unitPrice,
        cost: values.unitCost,
        depot: depotRes,
        name: relatedServiceLabel,
        product: selectedRow.product,
        relatedService: values.relatedService,
        addons: values.addons,
      },
    ];

    setTableItems((prev) => prev.map((row) => (row.id === selectedRow.id ? services[0] : row)));
    toggleEditModal();
  };

  useEffect(() => {
    if (canOpenEditModal && selectedRow && selectedDepot) {
      setEditModalOpen(true);
      setCanOpenEditModal(false); // Reset flag after opening
    }
  }, [canOpenEditModal, selectedRow, selectedDepot]);

  

  const toggleAddOnModal = (item?: any) => {
    setSearchTerm("");
    setAddOnOpen(!addOnOpen);
  };

  const removeDiscount = () => {
    setDiscount(null);
  };

  // Place this function near the top of your component
  const recalculateDiscount = (items: any[], discountPercent: number) => {
    let total = 0;

    items.forEach((service) => {
      // Main product/service: use price directly
      if (service.price !== undefined && service.quantity !== undefined) {
        total += Number(service.price) * Number(service.quantity);
      }
      // Add-ons: use price directly
      if (Array.isArray(service.addons)) {
        service.addons.forEach((addon: any) => {
          if (addon.price !== undefined && addon.quantity !== undefined) {
            total += Number(addon.price) * Number(addon.quantity);
          }
        });
      }
    });

    const discountAmount = +((total * discountPercent) / 100).toFixed(2);
    const discountTotal = +total.toFixed(2) - discountAmount;
    return { discountAmount, discountTotal, total: +total.toFixed(2) };
  };

  useEffect(() => {
    if (discount && discount.discountPercent) {
      const percent = discount.discountPercent;
      const { discountAmount, discountTotal } = recalculateDiscount(tableItems, percent);
      setDiscount((prev: any) => ({
        ...prev,
        amount: discountAmount,
        total: discountTotal,
      }));
    }
  }, [tableItems]); 

  return (
    <>
      <Header></Header>
      {!invoice && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {invoice && (
        <Page
          title="Edit Invoice"
          subtitle="Edit the Invoice to update it's details."
          backAction={
            <Link variant="back" href={`/invoices/${invoice.id}`}>
              <BackArrowIcon />
              {`Back to Invoice`}
            </Link>
          }
        >
          <Formik
            initialValues={{
              projectName: invoice.project.projectName,
              dueDate: invoice.dueDate ? moment.utc(invoice.dueDate).unix() : null,
              primaryClient: invoice.primaryClient.id,
              contactsCC: invoice.contactsCC,
              contactsBCC: invoice.contactsBCC,
              allowPartialPayment: invoice.allowPartialPayment || false,
              partialPaymentPercent: invoice.partialPaymentPercent || 75,
            }}
            onSubmit={submitUpdate}
            validationSchema={_validate}
          >
            {({ isSubmitting, errors, values, submitCount, setFieldValue, handleSubmit }) => (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1, justifyContent: "flex-end" }}>
                      <Link href={`/invoices/${invoice.id}`} sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Invoice
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit basic information about the Invoice.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "20px" }}>
                          <TextInput
                            name="projectName"
                            label="Project Name"
                            value={values.projectName}
                            onChange={(val: any) => setFieldValue("projectName", val)}
                            $errors={errors.projectName && submitCount > 0 ? errors.projectName : null}
                            $customStyles={{ flex: 1, marginBottom: "0px" }}
                            readonly={true}
                          />
                          <Flex sx={{ width: "50%", flexGrow: 1, marginBottom: "0px", gap: "12px", alignItems: "flex-end" }}>
                            <SelectInput
                              name="dueDatePreset"
                              label="Due Date"
                              value={dueDatePreset}
                              options={[
                                { value: "net30", label: "Net 30" },
                                { value: "net60", label: "Net 60" },
                                { value: "net90", label: "Net 90" },
                                { value: "custom", label: "Custom" },
                              ]}
                              onChange={(val: any) => {
                                setDueDatePreset(val || "");
                                if (val === "net30") {
                                  setFieldValue("dueDate", moment().add(30, "days").startOf("day").unix());
                                } else if (val === "net60") {
                                  setFieldValue("dueDate", moment().add(60, "days").startOf("day").unix());
                                } else if (val === "net90") {
                                  setFieldValue("dueDate", moment().add(90, "days").startOf("day").unix());
                                } else if (val === "custom") {
                                  if (!values.dueDate) {
                                    setFieldValue("dueDate", moment().add(30, "days").startOf("day").unix());
                                  }
                                } else if (!val) {
                                  setFieldValue("dueDate", null);
                                }
                              }}
                              $customStyles={{ flex: 1, marginBottom: "0px" }}
                              clearable
                            />
                            {values.dueDate && (
                              <SelectDate
                                name="dueDate"
                                value={values.dueDate}
                                onChange={(val: any) => {
                                  setFieldValue("dueDate", val);
                                  setDueDatePreset("custom");
                                }}
                                $errors={errors.dueDate && submitCount > 0 ? errors.dueDate : null}
                                $customStyles={{ flex: 1, marginBottom: "0px" }}
                                $removeMargin={true}
                              />
                            )}
                          </Flex>
                        </Flex>
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Contacts
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit contacts associated with the Invoice.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "20px" }}>
                          <Flex sx={{ flex: 1 }}>
                            <SelectInput
                              name="primaryClient"
                              label="Primary Client"
                              value={values.primaryClient}
                              options={companyClientOptions}
                              onChange={(val: any) => setFieldValue("primaryClient", val)}
                              $errors={errors.primaryClient && submitCount > 0 ? errors.primaryClient : null}
                              $customStyles={{ width: "100%" }}
                            />
                          </Flex>
                          <Flex sx={{ flex: 1 }}></Flex>
                        </Flex>

                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <Flex sx={{ flex: 1 }}>
                            <MultiText
                              name="contactsCC"
                              label="CC"
                              value={values.contactsCC}
                              onChange={(val: string[]) => setFieldValue("contactsCC", val)}
                              $errors={errors.contactsCC && submitCount > 0 ? errors.contactsCC : null}
                              $customStyles={{ width: "100%" }}
                            />
                          </Flex>
                          <Flex sx={{ flex: 1 }}>
                            <MultiText
                              name="bcc"
                              label="BCC"
                              value={values.contactsBCC}
                              onChange={(val: string[]) => setFieldValue("contactsBCC", val)}
                              $errors={errors.contactsBCC && submitCount > 0 ? errors.contactsBCC : null}
                              $customStyles={{ width: "100%" }}
                            />
                          </Flex>
                        </Flex>
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Items
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit the products and services associated with the Invoice.
                        </Subtext>
                      </Box>
                      <Box>
                        {tableItems && (
                          <TableBasic
                            data={tableItems}
                            columns={columns}
                            enablePaging={false}
                            enableSearch={false}
                            customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
                            disableSorting
                          />
                        )}
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Discounts
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Manage discounts associated with an invoice.
                        </Subtext>
                      </Box>
                      {discount ? (
                        <Grid gap={"30px"} columns={[1, "1fr 1fr"]}>
                          <Flex
                            sx={{
                              flexDirection: "column",
                              border: `1px solid ${themevals.colors.inputOutline}`,
                              borderRadius: "6px",
                              boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07);",
                              width: "100%",
                            }}
                          >
                            <Flex sx={{ flexDirection: "row", padding: "15px 20px 15px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                              <Flex sx={{ flex: 1, alignItems: "center" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000", marginRight: "5px" }}>{discount.discountName}</Box>
                              </Flex>
                              <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                                <BasicButton
                                  type="button"
                                  styles={{ margin: "0 0 0 10px" }}
                                  $iconHeight="14px"
                                  $iconWidth="14px"
                                  $loadingColor="#fff"
                                  keepText={true}
                                  onClick={() => removeDiscount()}
                                  scheme={"secondaryOutline"}
                                >
                                  <TrashIcon fill={"#000"} />
                                  Remove
                                </BasicButton>
                              </Flex>
                            </Flex>
                            <Grid gap={"30px"} columns={[1, "1fr 1fr"]} sx={{ padding: "20px" }}>
                              <Flex sx={{ flexDirection: "column" }}>
                                <Box
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: themevals.colors.dkgrey,
                                    marginBottom: "8px",
                                    lineHeight: "12px",
                                  }}
                                >
                                  Discount Amount
                                </Box>
                                <Box
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#000",
                                  }}
                                >
                                  {discount.amount !== undefined && discount.amount !== null ? Number(discount.amount).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "N/A"}
                                </Box>
                              </Flex>
                              <Flex sx={{ flexDirection: "column" }}>
                                <Box
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: themevals.colors.dkgrey,
                                    marginBottom: "8px",
                                    lineHeight: "12px",
                                  }}
                                >
                                  Discount Total
                                </Box>
                                <Box
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#000",
                                  }}
                                >
                                  {discount.total !== undefined && discount.total !== null ? Number(discount.total).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "N/A"}
                                </Box>
                              </Flex>
                            </Grid>
                          </Flex>
                        </Grid>
                      ) : (
                        <Flex sx={{ flex: 1, alignItems: "flex-start", justifyContent: "flex-start", padding: "20px 20px 20px 0" }}>
                          <Subtext fontSize={"14px"} fontWeight={"400"} fontFamily={"Poppins"}>
                            No discounts have been applied to this invoice.
                          </Subtext>
                        </Flex>
                      )}
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Payment Options
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Configure payment options for the client.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ flexDirection: "column", gap: "20px" }}>
                          <Flex sx={{ alignItems: "center" }}>
                            <Checkbox
                              name="allowPartialPayment"
                              label="Allow partial payment"
                              checked={values.allowPartialPayment}
                              onChange={() => {
                                const newValue = !values.allowPartialPayment;
                                setFieldValue("allowPartialPayment", newValue);
                                if (!newValue) {
                                  setFieldValue("partialPaymentPercent", 75);
                                }
                              }}
                            />
                          </Flex>
                          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"} color={themevals.colors.dkgrey}>
                            Client can pay a deposit instead of full amount (outside the 5-day early payment discount window).
                          </Subtext>
                          {values.allowPartialPayment && (
                            <Flex sx={{ gap: "20px", alignItems: "flex-end" }}>
                              <SelectInput
                                name="partialPaymentPercent"
                                label="Deposit Percentage"
                                value={values.partialPaymentPercent}
                                options={[
                                  { value: 50, label: "50%" },
                                  { value: 75, label: "75%" },
                                ]}
                                onChange={(val: any) => setFieldValue("partialPaymentPercent", Number(val))}
                                $customStyles={{ width: "200px" }}
                              />
                              <Box sx={{ paddingBottom: "12px" }}>
                                <Subtext marginTop={0} marginBottom={0} fontSize={"14px"} fontWeight={"500"} fontFamily={"Poppins"} color={themevals.colors.black}>
                                  Deposit amount:{" "}
                                  {(() => {
                                    const total = tableItems.reduce((acc, service) => {
                                      let serviceTotal = (service.price || 0) * (service.quantity || 0);
                                      if (Array.isArray(service.addons)) {
                                        service.addons.forEach((addon: any) => {
                                          serviceTotal += (addon.price || 0) * (addon.quantity || 0);
                                        });
                                      }
                                      return acc + serviceTotal;
                                    }, 0);
                                    const depositAmount = (total * (values.partialPaymentPercent / 100)).toFixed(2);
                                    return Number(depositAmount).toLocaleString("en-US", { style: "currency", currency: "USD" });
                                  })()}
                                </Subtext>
                              </Box>
                            </Flex>
                          )}
                        </Flex>
                      </Box>
                    </Grid>
                  </Tab>
                </Tabs>
              </Form>
            )}
          </Formik>
          <Modal isOpen={editModalOpen} toggle={() => toggleEditModal()} centered size={user.role !== null ? "md" : "sm"}>
          <Formik
              initialValues={{
                relatedService: selectedService,
                addons: selectedAddons,
                quantity: selectedRow?.quantity,
                markup: selectedRow?.markup,
                unitPrice: selectedRow?.price,
                product: selectedRow?.product,
                unitCost: selectedRow?.cost,
              }}
              onSubmit={(values) => submitItemEdit(values)}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                errors,
                submitCount,
              }: {
                isSubmitting: boolean;
                submitCount: number;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                errors: any;
              }) => {
                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (!product.addon) return false;
                    const existing = values.addons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                  console.log("selectedRow", selectedRow);

                const submitRemoveAddonItem = (addonToRemove: any) => {
                  const updated = values.addons.map((addon: any) => (addon.entityId === addonToRemove.entityId ? { ...addon, quantity: 0 } : addon));
                  setFieldValue("addons", updated);
                  setSelectedAddons(updated);
                };

                useEffect(() => {
                  const mergedAddons = [...values.addons];
                  console.log("mergedAddons", mergedAddons);
                  console.log("selectedAddons", selectedAddons);

                  selectedAddons.forEach((newAddon) => {
                    const existingIndex = mergedAddons.findIndex((a) => a.entityId === newAddon.entityId);
                    console.log("existingIndex", mergedAddons[existingIndex]);

                    if (existingIndex > -1) {
                      mergedAddons[existingIndex] = {
                        ...mergedAddons[existingIndex],
                        quantity: newAddon.quantity ?? mergedAddons[existingIndex].quantity,
                        price: newAddon.markup ?? mergedAddons[existingIndex].price,
                        cost: newAddon.cost ?? mergedAddons[existingIndex].cost,
                      };
                    } else {
                      mergedAddons.push({ ...newAddon, quantity: newAddon.quantity || 1, new: true });
                    }
                  });
                  console.log("mergedAddons", mergedAddons);
                  setFieldValue("addons", mergedAddons);
                }, [selectedAddons]);

                const relatedServiceIdsInUse = tableItems.filter((row) => row.id !== selectedRow?.id).map((row) => row.serviceId);

                const filteredServiceOptions = depotServiceOptions.filter((option) => !relatedServiceIdsInUse.includes(option.value));

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleEditModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Edit Invoice item</ModalTitle>
                        <ModalSubtitle>{"Edit an item related to an Invoice."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <Flex sx={{ gap: "20px", flexDirection: user.role !== null ? "row" : "column" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="bulkProduct"
                            label="Bulk Product"
                            value={values.product.id}
                            options={[{ value: values.product.id, label: values.product.name }]}
                            $errors={errors.bulkProduct && submitCount > 0 ? errors.bulkProduct : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            disabled
                          />
                        </Box>
                        {user.role === null && (
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="relatedService"
                              label="Related Service"
                              value={values.relatedService}
                              options={filteredServiceOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              disabled={user.role === null}
                              onChange={(val: any) => setFieldValue("relatedService", val)}
                            />
                          </Box>
                        )}
                        {user.role === null && (
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="quantity"
                              label="Quantity"
                              type="number"
                              value={values.quantity}
                              onChange={(val: any) => setFieldValue("quantity", val)}
                              $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                              suffix={values.product.unit}
                            />
                          </Box>
                        )}
                        {user.role !== null && (
                          <Box sx={{ flex: 1 }}>
                            <SelectInput
                              name="relatedService"
                              label="Related Service"
                              value={values.relatedService}
                              options={filteredServiceOptions}
                              $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                              disabled={user.role === null}
                              onChange={(val: any) => setFieldValue("relatedService", val)}
                            />
                          </Box>
                        )}
                      </Flex>
                      
                      {user.role !== null && (
                        <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="unitPrice"
                              label="Unit Price"
                              type="number"
                              value={values.unitPrice}
                              onChange={(val: any) => setFieldValue("unitPrice", val)}
                              prefix={"$"}
                              $errors={errors?.unitPrice && submitCount > 0 ? errors.unitPrice : null}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <TextInput
                              name="quantity"
                              label="Quantity"
                              type="number"
                              value={values.quantity}
                              onChange={(val: any) => setFieldValue("quantity", val)}
                              $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                            />
                          </Box>
                        </Flex>
                      )}
                      <Flex>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Add-on Products
                        </Subtext>
                      </Flex>
                      <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", flexDirection: "column", marginBottom: "30px" }}>
                        <Flex sx={{ padding: "20px", gap: "20px", flexDirection: "column" }}>
                          {values.addons.filter((addon: any) => addon.quantity > 0).length > 0 &&
                            values.addons.map((addon: any, index: number) =>
                              addon.quantity > 0 ? (
                                <Flex
                                  key={index}
                                  sx={{
                                    gap: "10px",
                                    justifyContent: "flex-start",
                                    borderRadius: "5px",
                                    border: `1px solid ${themevals.colors.mdgrey}`,
                                    padding: "10px 15px",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "flex-start" }}>
                                    <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="500">
                                      {addon.name}
                                    </P>
                                  </Flex>
                                  <Flex sx={{ alignItems: "center", justifyContent: "flex-end", flex: user.role !== null ? 2 : 1, gap: "20px" }}>
                                    {user.role !== null && (
                                      <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                        <TextInput
                                          name={`addons[${index}].baseCost`}
                                          value={addon.baseCost}
                                          type="number"
                                          label="Base Cost"
                                          prefix={"$"}
                                          $customStyles={{ marginBottom: "0px" }}
                                          onChange={(val: any) => setFieldValue(`addons[${index}].baseCost`, val)}
                                          $errors={errors?.addons?.[index].baseCost && submitCount > 0 ? errors?.addons?.[index].baseCost : null}
                                        />
                                      </Flex>
                                    )}
                                     {user.role !== null && (
                                      <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                        <TextInput
                                          name={`addons[${index}].price`}
                                          value={addon.price}
                                          type="number"
                                          label="Price"
                                          prefix={"$"}
                                          $customStyles={{ marginBottom: "0px" }}
                                          onChange={(val: any) => setFieldValue(`addons[${index}].price`, val)}
                                          $errors={errors?.addons?.[index].price && submitCount > 0 ? errors?.addons?.[index].price : null}
                                        />
                                      </Flex>
                                    )}
                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].quantity`}
                                        value={addon.quantity}
                                        type="number"
                                        label={user.role !== null ? "Quantity" : ""}
                                        min={1}
                                        suffix={addon.unit}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].quantity`, val)}
                                        $errors={errors?.addons?.[index].quantity && submitCount > 0 ? errors?.addons?.[index].quantity : null}
                                        onKeyDown={(e: any) => {
                                          if (e.key === "0" && (e.currentTarget.value === "" || e.currentTarget.value === "0")) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onBlur={(e: any) => {
                                          const val = parseFloat(e.target.value);
                                          if (isNaN(val) || val < 1) {
                                            setFieldValue(`addons[${index}].quantity`, 1);
                                          }
                                        }}
                                      />
                                    </Flex>
                                   
                                  </Flex>
                                  {user.role !== null && (
                                    <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", paddingLeft: "10px", alignSelf: "stretch", width: "30px" }}>
                                      <Flex sx={{ alignSelf: "stretch", alignItems: "flex-end", paddingBottom: "10px" }}>
                                        <Box sx={{ cursor: "pointer" }} onClick={() => submitRemoveAddonItem(addon)}>
                                          <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                        </Box>
                                      </Flex>
                                    </Flex>
                                  )}
                                </Flex>
                              ) : null
                            )}
                          {values.addons.filter((addon: any) => addon.quantity > 0).length === 0 && (
                            <P fontFamily="Poppins" fontSize="14px" color={`${themevals.colors.dkgrey}`}>
                              There are no add-on products associated with this item.
                            </P>
                          )}
                        </Flex>
                        {availableAddons.length > 0 && (
                          <Flex sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", justifyContent: "flex-end" }}>
                            <BasicButton
                              type="button"
                              scheme="secondaryOutline"
                              $submitting={isSubmitting}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#000"
                              keepText={true}
                              onClick={() => toggleAddOnModal()}
                            >
                              {!isSubmitting && <PlusIcon fill={"#000"} />}
                              Add product
                            </BasicButton>
                          </Flex>
                        )}
                      </Flex>
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleEditModal()} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save item
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
          <Modal isOpen={addOnOpen} toggle={() => toggleAddOnModal()} centered size={"sm"}>
            <Formik
              initialValues={{ relatedService: selectedRow?.name, addons: selectedRow?.addons }}
              onSubmit={() => {
                setAddOnOpen(false);
              }}
              enableReinitialize
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                submitCount,
                errors,
              }: {
                isSubmitting: boolean;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                submitCount: number;
                errors: any;
              }) => {
                const availableAddons =
                  selectedDepot?.products?.filter((product) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                console.log("selectedAddons", selectedAddons);
                console.log("availableAddons", availableAddons);
                console.log("selectedDepot", selectedDepot?.products);

                const toggleSelectAll = () => {
                  if (newAddons.length === availableAddons.length) {
                    // If all are selected, deselect all
                    setNewAddons([]);
                  } else {
                    // Select all available add-ons
                    setNewAddons(
                      availableAddons.map((product) => ({
                        ...product,
                        addonId: product.id,
                        quantity: 1,
                        cost: product.cost,
                      }))
                    );
                  }
                };

                const handleAddSelectedAddons = () => {
                  setSelectedAddons((prevAddons) => [...prevAddons, ...newAddons]); // Merge selections into first modal
                  setNewAddons([]); // Clear temporary selections
                  setAddOnOpen(false); // Close the modal
                };

                const handleAddonSelect = (product: any) => {
                  const existing = selectedAddons.find((addon: any) => addon.entityId === product.entityId);
                  console.log("product", product);
                  setNewAddons((prev) =>
                    prev.some((addon) => addon.id === product.id)
                      ? prev.filter((addon) => addon.id !== product.id) // Remove if already selected
                      : [
                          ...prev,
                          {
                            ...product,
                            entityId: product.id,
                            quantity: 1,
                            baseCost: existing?.baseCost,
                            markup: existing?.markup,
                          },
                        ]
                  );
                };

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleAddOnModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Add products</ModalTitle>
                        <ModalSubtitle>{"Add available products at this depot."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                      <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center", paddingLeft: "11px" }}>
                        <Checkbox label="Select All" onChange={toggleSelectAll} checked={newAddons.length === availableAddons.length && availableAddons.length > 0} />
                      </Flex>
                      {availableAddons.map((product, idx) => (
                        <Flex
                          key={product.id}
                          sx={{
                            gap: "1rem",
                            alignItems: "center",
                            marginBottom: "10px",
                            border: `1px solid ${values.addons.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                            borderRadius: "6px",
                            height: "38px",
                            padding: "0px 10px",
                          }}
                        >
                          <Checkbox label={product.name} checked={newAddons.some((addon) => addon.id === product.id)} onChange={() => handleAddonSelect(product)} labelSize="14px" />
                        </Flex>
                      ))}
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => setAddOnOpen(false)} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="button" onClick={() => handleAddSelectedAddons()} $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <PlusIcon fill={"#fff"} />}
                        Add products
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
        </Page>
      )}
    </>
  );
};

export default InvoiceEdit;
